import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListResponse, ModuleSpecification } from '@ca/core/models';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { FileItem } from 'ng2-file-upload';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { createListResponseWithTotalCount } from '../../../utils/utils';
import { FileDeleteResponseDto, FileModel } from '../models/file-model.model';
import { Folder } from '../models/folder.model';
import { ListResult } from '../models/list-result';
import { ListSearch } from '../models/list-search.model';
import { defaultPageNumber, defaultPageSize, defaultSortBy } from './../../shared/constants/constants';
import { DataService } from './abstract-services/data.service';
import { ExperimentalFeaturesService } from './experimental-features.service';
import { UserNotificationService } from './user-notification.service';

@Injectable()
export class FileService extends DataService<FileModel> {
    protected apiUrl = 'documents/files';
    private readonly filesBaseUrl = '/api/documents/files/';
    private readonly MEDfoldersBaseUrl = '/api/documents/rdp/folders/';
    private readonly foldersBaseUrl = '/api/documents/folders/';
    private readonly MEDfileBaseUrl = '/api/documents/rdp/';

    constructor(
        protected http: HttpClient,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private userNotificationService: UserNotificationService,
        private translateService: TranslateService,
    ) {
        super();
    }

    getListPublicResultFiles$(shareId: string): Observable<ListResult<FileModel>> {
        return from(this.getList(new HttpParams().set('access', 'read'), `shared/${shareId}`));
    }

    getFilesByWorkflowRunId$(workflowRunId: string): Observable<ListResult<FileModel>> {
        return from(this.getList(new HttpParams().set('access', 'read').set('page[size]', '5000').set('page[number]', '1'), `workflowrun/${workflowRunId}`));
    }

    getFileById$(fileId: string): Observable<FileModel> {
        const params = new HttpParams().set('access', 'read');
        return this.http.get(`${this.filesBaseUrl}${fileId}`, { params }) as Observable<FileModel>;
    }

    getFilesWithMetadata$(searchParams?: ListSearch): Observable<ListResponse<FileModel>> {
        let httpParams: HttpParams;
        if (searchParams) {
            httpParams = this.cleanHttpParams(
                new HttpParams()
                    .set('page[number]', searchParams?.pageNumber.toString() || defaultPageNumber.toString())
                    .set('page[size]', searchParams?.pageSize.toString() || defaultPageSize.toString())
                    .set('sort', searchParams?.sortBy || defaultSortBy)
                    .set('access', 'read')
                    .set('root', 'true')
                    .set('search', searchParams?.searchQuery || ''),
            );
        } else {
            httpParams = this.cleanHttpParams(
                new HttpParams().set('page[number]', '1').set('page[size]', '1').set('access', 'read').set('root', 'true').set('search', ''),
            );
        }

        return this.http
            .get<{ data: FileModel[]; meta: { totalRecordCount: number } }>(`${this.filesBaseUrl}`, { headers: DataService.contentType, params: httpParams })
            .pipe(
                map((result: { data: FileModel[]; meta: { totalRecordCount: number } }) => {
                    const mappedData = result.data.map(entity => Object.assign(this.createNewInstance(), entity));

                    return createListResponseWithTotalCount(mappedData, result.meta.totalRecordCount);
                }),
            );
    }

    /**
     * Saves a file URL to our DB
     *
     * @param fileItem the file item
     * @param signFileUrl - If set to true, the fileUrl returned will not contain a URL, but a token
     *                      The token is a URL with a sas-parameter (signature), which means that the
     *                      file url can be opened
     */
    addFile$(fileItem: FileItem, signFileUrl: boolean = false): Observable<FileModel> {
        const accessParam: string = signFileUrl === true ? '?access=read' : '';
        return this.http.post<FileModel>(
            `${this.filesBaseUrl}${accessParam}`,
            { name: fileItem.file.name, sizeInBytes: fileItem.file.size },
            { headers: DataService.plainJsonContentType },
        );
    }

    move$(targetFolderId: string, fileIds: string[], folderIds: string[] = []): Observable<void> {
        return this.http.post<void>(`${this.foldersBaseUrl}move`, { targetFolderId, fileIds, folderIds });
    }

    deleteFiles$(ids: string[]): Observable<FileDeleteResponseDto[]> {
        return this.http.request<FileDeleteResponseDto[]>('delete', `${this.filesBaseUrl}`, {
            body: { ids: ids },
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        });
    }

    deleteFileById$(id: string): Observable<FileDeleteResponseDto> {
        return this.http.delete<FileDeleteResponseDto>(`${this.filesBaseUrl}${id}`);
    }

    /**
     * Triggers a download
     */
    downloadFile(fileName: string, url: string): void {
        saveAs(url, fileName);
    }

    downloadTextfile(text: string, fileName: string): void {
        if (!fileName.endsWith('.txt')) {
            fileName += '.txt';
        }

        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, fileName);
    }

    downloadJSON(json: ModuleSpecification, fileName: string): void {
        if (!fileName.endsWith('.json')) {
            fileName += '.json';
        }

        const content: string = JSON.stringify(json, undefined, 4);

        const blob = new Blob([content], { type: 'text/json;charset=utf-8' });
        saveAs(blob, fileName);
    }

    checkFilesDeletionStatus(fileIdsToBeDeleted: string[], results: FileDeleteResponseDto[]): void {
        const fileDeletedSuccessfullyMessage = this.translateService.instant('MY_WORKSPACE.MY_FILES.FILE_DELETED_SUCCESSFULLY');
        const filesDeletedSuccessfullyMessage = this.translateService.instant('MY_WORKSPACE.MY_FILES.FILES_DELETED_SUCCESSFULLY');
        const filesFailedToDelete: FileDeleteResponseDto[] = results.filter(f => f.error != null);
        let errorMessage: string = '';

        for (let i = 0; i < filesFailedToDelete.length; i++) {
            errorMessage += filesFailedToDelete[i].error! + (i < filesFailedToDelete.length - 1 ? '\n' : '');
        }

        if (fileIdsToBeDeleted.length === 1 && filesFailedToDelete.length === 1) {
            this.userNotificationService.showError(filesFailedToDelete[0].error!);
        } else if (fileIdsToBeDeleted.length === filesFailedToDelete.length) {
            this.userNotificationService.showError(this.translateService.instant('MY_WORKSPACE.MY_FILES.FILES_COULD_NOT_BE_DELETED', { errorMessage }));
        } else if (filesFailedToDelete.length > 0) {
            this.userNotificationService.showError(this.translateService.instant('MY_WORKSPACE.MY_FILES.SOME_FILES_COULD_NOT_BE_DELETED', { errorMessage }));
        } else {
            this.userNotificationService.showSuccess(fileIdsToBeDeleted.length > 1 ? filesDeletedSuccessfullyMessage : fileDeletedSuccessfullyMessage);
        }
    }

    checkFolderDeletionStatus(
        response: {
            succeeded: boolean;
            fileDeletionLog: FileDeleteResponseDto[];
        },
        folders: Folder[],
    ): void {
        const folderDeletedSuccessfullyMessage = this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_DELETED_SUCCESSFULLY');
        const foldersDeletedSuccessfullyMessage = this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDERS_DELETED_SUCCESSFULLY');
        const folderFailedToDelete: FileDeleteResponseDto[] = response.fileDeletionLog.filter((file: FileDeleteResponseDto) => file.error != null);
        let errorMessage: string = '';

        folderFailedToDelete.forEach((file, index) => {
            errorMessage += file.error! + (index < folderFailedToDelete.length - 1 ? '\n' : '');
        });

        if (folderFailedToDelete.length === 1) {
            this.userNotificationService.showError(folderFailedToDelete[0].error!);
        } else if (folderFailedToDelete.length > 0) {
            this.userNotificationService.showError(this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDERS_COULD_NOT_BE_DELETED', { errorMessage }));
        } else {
            this.userNotificationService.showSuccess(folders.length > 1 ? foldersDeletedSuccessfullyMessage : folderDeletedSuccessfullyMessage);
        }
    }

    getFolder$(folderId?: string, searchParams?: ListSearch): Observable<Folder<FileModel>> {
        const httpParams = this.buildHttpParams(searchParams);

        const baseUrl = this.experimentalFeaturesService.isMEDProjectActive ? this.MEDfoldersBaseUrl : this.foldersBaseUrl;

        return this.http.get<Folder>(`${baseUrl}search${folderId ? `/${folderId}` : ''}`, { params: httpParams }).pipe(
            map(folder => {
                if (!this.experimentalFeaturesService.isMEDProjectActive) {
                    return Folder.createFileModelInstance(folder as Folder<FileModel>);
                } else {
                    return Folder.createFileModelMedInstance(folder as Folder<FileModel>);
                }
            }),
        );
    }
    deleteBySearchParams$(searchQuery: string, folderId?: string): Observable<FileDeleteResponseDto[]> {
        let httpParams: HttpParams;
        if (searchQuery) {
            httpParams = this.cleanHttpParams(new HttpParams().set('text', searchQuery));
            return this.http.delete<FileDeleteResponseDto[]>(`${this.filesBaseUrl}delete${folderId ? `/${folderId}` : ''}`, {
                params: httpParams,
            });
        }
        return of();
    }

    createFolder$(targetFolderId: string, name: string): Observable<Folder<FileModel>> {
        return this.http.post<Folder>(`${this.foldersBaseUrl}${targetFolderId}/${name}`, { headers: DataService.plainJsonContentType }).pipe(
            map(folder => {
                if (!this.experimentalFeaturesService.isMEDProjectActive) {
                    return Folder.createFileModelInstance(folder as Folder<FileModel>);
                } else {
                    return Folder.createFileModelMedInstance(folder as Folder<FileModel>);
                }
            }),
        );
    }

    createNestedFolders$(folders: string[], targetFolderId?: string): Observable<Folder<FileModel>> {
        const targetFolderUrl = targetFolderId ? targetFolderId + '/' : '';
        return this.http
            .post<Folder>(`${this.foldersBaseUrl}${targetFolderUrl}subfolders`, { names: folders }, { headers: DataService.plainJsonContentType })
            .pipe(
                map(folder => {
                    if (!this.experimentalFeaturesService.isMEDProjectActive) {
                        return Folder.createFileModelInstance(folder as Folder<FileModel>);
                    } else {
                        return Folder.createFileModelMedInstance(folder as Folder<FileModel>);
                    }
                }),
            );
    }

    renameFolder$(folder: Folder, name: string): Observable<Folder> {
        return this.http.post<Folder>(`${this.foldersBaseUrl}${folder.id}/rename`, { name, headers: DataService.plainJsonContentType });
    }

    deleteFolder$(folderId: string): Observable<{ succeeded: boolean; fileDeletionLog: FileDeleteResponseDto[] }> {
        return this.http.delete<{ succeeded: boolean; fileDeletionLog: FileDeleteResponseDto[] }>(`${this.foldersBaseUrl}${folderId}`);
    }

    checkFolderExistence$(folders: string[], parentFolderId?: string): Observable<Folder | null> {
        const parentFolderUrl = parentFolderId ? parentFolderId + '/' : '';
        return this.http
            .post<Folder>(`${this.foldersBaseUrl}existence/${parentFolderUrl}subfolders`, { names: folders }, { headers: DataService.plainJsonContentType })
            .pipe(
                switchMap(f => {
                    if (f != null) {
                        return of(f);
                    }
                    return of(null);
                }),
            );
    }

    updateTagsMetadata$(fileId: string, metadataUpdate: { tags: string[] }): Observable<void> {
        const url = `${this.MEDfileBaseUrl}tags/files/${fileId}?`;
        return this.http.put<void>(url, metadataUpdate);
    }

    updateStudyId$(fileId: string, studyIdUpdate: string): Observable<void> {
        const url = `${this.MEDfileBaseUrl}studyid/files/${fileId}`;
        return this.http.put<void>(url, studyIdUpdate, { headers: { 'Content-Type': 'application/json' } });
    }

    protected createNewInstance(): FileModel {
        return new FileModel();
    }

    private buildHttpParams(searchParams?: ListSearch): HttpParams {
        const defaultPageNumber = 1;
        const defaultPageSize = 20;
        const defaultSortBy = 'title';

        const httpParams = new HttpParams()
            .set('page[number]', (searchParams?.pageNumber || defaultPageNumber).toString())
            .set('page[size]', (searchParams?.pageSize || defaultPageSize).toString())
            .set('sort', searchParams?.sortBy || defaultSortBy)
            .set('text', searchParams?.searchQuery || '')
            .set('access', 'read');

        return this.cleanHttpParams(httpParams);
    }
}
